import React, { FC, RefObject, useState } from 'react';
import classNames from 'classnames';

import { ClosePlayerButton } from '../../../feature-player/ui/skin/close-player-button/component';
import { BaseControlName } from '../../../feature-player/types/controls';
import { SubtitlesProvider } from '../../../feature-player/ui/subtitles/subtitles-provider/components';
import { ContextMenuContainer } from '../../../feature-player/ui/skin/context-menu/container';
import { useKeyboard } from '../../hooks/use-keyboard';
import { GlobalKeyboardHandlerTogglersProvider } from '../../../feature-player/contexts/global-keyboard-handler-togglers';
import { SubtitlesPortalRoot } from '../../../feature-player/ui/subtitles/subtitles-portal-root/component';

import { ChannelPlayerControlPanel } from './ChannelPlayerControlPanel';
import { ChannelPlayerMeta } from './ChannelPlayerMeta';
import styles from './ChannelPlayerSkin.module.css';
import { ChannelLayout } from './ChannelLayout';
import { ChannelSessionStopScreen } from './ChannelSessionStopScreen';
import { ChannelPlayButton } from './ChannelPlayButton';
import { ChannelAgeRestriction } from './ChannelAgeRestriction';

interface ChannelPlayerSkinProps {
    contentId: string;
    onClose: () => void;
    className?: string;
    psid: string;
    rootContainerRef: RefObject<HTMLDivElement>;
}
export const ChannelPlayerSkin: FC<ChannelPlayerSkinProps> = ({
    contentId,
    onClose,
    className,
    rootContainerRef,
    psid,
}) => {
    const [isSessionStopped, setIsSessionStopped] = useState<boolean>(false);
    useKeyboard({ onClose });

    return (
        <SubtitlesProvider>
            <GlobalKeyboardHandlerTogglersProvider>
                <ChannelLayout
                    rootContainerRef={rootContainerRef}
                    className={classNames(styles.layout, className)}
                    renderTopControls={() => (
                        <ClosePlayerButton
                            name={BaseControlName.CLOSE}
                            onClick={onClose}
                            className={styles.closeButton}
                            contentId={contentId}
                        />
                    )}
                    renderBottomControls={
                        !isSessionStopped
                            ? ({ setIsHidingControlsBlocked }) => (
                                  <div className={styles.bottomControls}>
                                      <ChannelPlayerMeta
                                          contentId={contentId}
                                          className={styles.meta}
                                      >
                                          {({ onEnlargementChanged }) => (
                                              <ChannelPlayerControlPanel
                                                  contentId={contentId}
                                                  psid={psid}
                                                  setIsHidingControlsBlocked={
                                                      setIsHidingControlsBlocked
                                                  }
                                                  onEnlargementChanged={onEnlargementChanged}
                                              />
                                          )}
                                      </ChannelPlayerMeta>
                                  </div>
                              )
                            : undefined
                    }
                    renderControls={({ isControlsVisible }) => (
                        <>
                            <ChannelSessionStopScreen
                                contentId={contentId}
                                onIsSessionStoppedChanged={setIsSessionStopped}
                            />
                            {!isControlsVisible && <ChannelAgeRestriction contentId={contentId} />}
                            <ChannelPlayButton className={styles.playButton} />
                            <SubtitlesPortalRoot isControlsVisible={isControlsVisible} />
                        </>
                    )}
                    renderPopups={() =>
                        rootContainerRef && (
                            <ContextMenuContainer rootContainerRef={rootContainerRef} />
                        )
                    }
                />
            </GlobalKeyboardHandlerTogglersProvider>
        </SubtitlesProvider>
    );
};
